import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import FormLabel from "@material-ui/core/FormLabel";
import FormControl from "@material-ui/core/FormControl";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 20,
  },
  links: {
    "&": {
      boxSizing: "border-box",
      flex: 0,
      overflow: "hidden",
      padding: 0,
      transition: "all 0.5s",
      transform: "scale(0)",
      transformOrigin: "left center",
      wordBreak: "break-all",
    },
    "&.expanded": {
      flex: 0.33,
      padding: "20px",
      transform: "scale(1)",
    },
    "& .MuiFormControlLabel-root": {
      marginBottom: "20px",
    },
  },
  textarea: {
    flex: 0.33,
    minWidth: "400px",
    padding: 20,
    width: "400px",
  },
  content: {
    "&": {
      alignItems: "flex-start",
      display: "flex",
      minHeight: "540px",
    },
  },
}));

function App() {
  const classes = useStyles();
  const [originalHtml, setOriginalHtml] = React.useState("");
  const [possibleClickUrls, setPossibleClickUrls] = React.useState([]);
  const [clickUrls, setClickUrls] = React.useState([]);
  const [result, setResult] = React.useState([]);
  const isClickUrl = /^https?:\/\//.test(originalHtml);
  const originalHtmlLabel =
    originalHtml.length < 9
      ? undefined
      : isClickUrl
      ? "Original Click URL"
      : "Original Creative Code";
  const resultPlaceholder = !originalHtml
    ? "Result will be shown here"
    : possibleClickUrls.length
    ? "Select which of the URLs in the creative is the Click URL"
    : "No Click URL found";
  const resultLabel = !clickUrls.length
    ? undefined
    : isClickUrl
    ? "Result Click URL"
    : "Result Creative Code";

  function handleOriginalHtmlChange(event) {
    const { value } = event.target;
    setOriginalHtml(value);
    const nextPossibleClickUrls = (
      value.match(/https?(:\/\/|%3[Aa]%2[Ff]%2[Ff])[^'"]+/g) || []
    ).filter(
      (url) =>
        !/\.(png|jpg|css|map|ttf|otf|woff|svg|jpeg|eot|js|bmp|gif|webm)$/.test(
          url
        )
    );
    setPossibleClickUrls(nextPossibleClickUrls);
    if (nextPossibleClickUrls.length && nextPossibleClickUrls[0] === value) {
      const nextClickUrls = [...nextPossibleClickUrls];
      setClickUrls(nextClickUrls);
      setResult(makeResult(value, nextClickUrls));
    } else {
      setClickUrls([]);
      setResult("");
    }
  }

  function handleClickUrlChange(clickUrl) {
    return (event) => {
      const { checked } = event.target;
      const nextClickUrls = checked
        ? clickUrls.concat(clickUrl)
        : clickUrls.filter((c) => c !== clickUrl);
      setClickUrls(nextClickUrls);
      setResult(makeResult(originalHtml, nextClickUrls));
    };
  }

  function makeResult(nextOriginalHtml, nextClickUrls) {
    if (!nextClickUrls.length) {
      return "";
    }
    return nextClickUrls
      .map((c) => [c, formatClickUrl(c)])
      .reduce((r, replaceArgs) => r.replace(...replaceArgs), nextOriginalHtml);
  }

  function formatClickUrl(url) {
    const alreadyEncoded = /^https?%3[Aa]%2[Ff]%2[Ff]/.test(url);
    if (!alreadyEncoded) {
      url = encodeURIComponent(url.trim())
        .replace(/%7[Bb]/g, "{")
        .replace(/%7[Dd]/g, "}");
    }
    return `{CLICK_URL}${url}`;
  }

  return (
    <div className={classes.root}>
      <p>
        This tool prepares HTML creatives for use at{" "}
        <a
          href="http://console.kayzen.io"
          target="_blank"
          rel="noopener noreferrer"
        >
          Kayzen Console
        </a>
        . Add the necessary Kayzen macros to your Click URL before entering the
        info below
      </p>
      <div className={classes.content}>
        <div className={classes.textarea}>
          <TextField
            label={originalHtmlLabel}
            placeholder="Enter the Creative Code or just the Click URL"
            multiline
            value={originalHtml}
            fullWidth
            onChange={handleOriginalHtmlChange}
            rows="20"
            variant="outlined"
          />
        </div>
        <div
          className={
            classes.links +
            (possibleClickUrls.length && !isClickUrl ? " expanded" : "")
          }
        >
          <FormControl component="fieldset">
            <FormLabel component="legend">Select the Click URL</FormLabel>
            <FormGroup>
              {possibleClickUrls.map((possibleClickUrl, index) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      checked={clickUrls.includes(possibleClickUrl)}
                      value={possibleClickUrl}
                      onChange={handleClickUrlChange(possibleClickUrl)}
                    />
                  }
                  label={possibleClickUrl}
                />
              ))}
            </FormGroup>
          </FormControl>
        </div>
        <div className={classes.textarea}>
          <TextField
            label={resultLabel}
            placeholder={resultPlaceholder}
            multiline
            onFocus={(event) => event.target.select()}
            value={result}
            fullWidth
            rows="20"
            variant="outlined"
          />
        </div>
      </div>
    </div>
  );
}

export default App;
